/**
 * The data explorer's debug reads: the Durable Object types they inspect and their `limit`
 * bounds, which the admin contract's request schemas validate and every class's debug read
 * (`debug-read.ts`, `debug-redaction.ts` in the backend) applies again; and the count capability's
 * answers, plain types no schema or redaction re-checks: counts, and a hub's environment (HubDO and
 * OrgDO build the counts; the route adds a production hub's publish state, also counts).
 * Declared apart from the admin contract, which imports them, so every Durable Object class loads
 * them without that contract's schemas (gh #5083, decision 61;
 * `scripts/check-backend-startup-graph.ts`). The contracts barrel exports them.
 */

import { z } from 'zod';

/** Valid DO types for debug reads. Shared source of truth — re-exported as `DoType` in debug-redaction.ts. */
export const dataExplorerDebugDoType = z.enum(['hub', 'org', 'conversation', 'user', 'platform', 'thread', 'community', 'contact_book', 'batch']);

export type DataExplorerDebugDoType = z.infer<typeof dataExplorerDebugDoType>;

/** Hard cap on `limit` query param — applied in both Zod and the DO RPC. */
export const DEBUG_READ_MAX_LIMIT = 100;
/** Default `limit` when the caller omits it. */
export const DEBUG_READ_DEFAULT_LIMIT = 20;

// The count capability's answers (wayai-pro/wayai#5142 step 5's census): declared here, beside the
// DO types, because HubDO and OrgDO compute them and load this module without the admin contract.

/**
 * The stored texts an agent acts on, by family: an agent's own four, an experiment arm's override
 * of them, and the hub texts owner decision 192 named.
 */
export const DEBUG_TEXT_FAMILIES = [
  'agent_instructions',
  'agent_additional_context',
  'agent_system_message',
  'agent_monitor_notes',
  'experiment_arm_overrides',
  'automation_instructions',
  'kanban_instructions',
  'tool_texts',
  'resource_descriptions',
  'folder_descriptions',
] as const;
export type DebugTextFamily = (typeof DEBUG_TEXT_FAMILIES)[number];

/** One text family's resource placeholder calls. */
export interface DebugPlaceholderCounts {
  /** The family's non-empty texts read. */
  texts: number;
  /** `{{files('kb')}}` / `{{resources('kb')}}`: the knowledge catalog. */
  knowledge_catalog: number;
  /** `{{file_content(resources/…)}}`: a path under the retired knowledge mount, which resolves to nothing. */
  knowledge_file_content: number;
  /** `{{files()}}` / `{{resources()}}` with no kind: it lists knowledge only while knowledge is linked, so it is not a knowledge placeholder. */
  unfiltered_catalog: number;
}

/** What each stored value is, by shape alone (`debug-secret-shapes.ts`). */
export interface DebugSecretValueShapes {
  /** Only `{{...}}`, alone or behind an auth scheme: `Bearer {{access_token}}`. */
  placeholder: number;
  /** A `{{...}}` beside other text, which may hold a key. */
  mixed: number;
  /** Starts `enc:v<n>:`, an envelope's form, which is no proof it decrypts. */
  enc: number;
  /** Text with neither, or a container nested too deep to read. With `mixed`, where a raw secret is expected; a zero is no proof none is stored. */
  literal: number;
  empty: number;
  /** A number, a boolean or null. */
  non_string: number;
}

/** One stored secret-bearing column, over every row it has, soft-deleted included: the key is still stored. */
export interface DebugSecretColumnShape {
  rows: number;
  /** Rows holding at least one entry: `{}`, `[]`, `null` and NULL hold none. */
  non_empty: number;
  entries: number;
  values: DebugSecretValueShapes;
}

/**
 * Stored summary schemas, judged by the summarizer schema invariant, which requires every anchor
 * on its own (gh #5744): verdicts as counts, never the schema. A row with no schema, one that is
 * not JSON, or one that does not compile (a probe reaches an unresolved `$ref`) passes neither.
 */
export interface DebugSummarySchemaCounts {
  total: number;
  /** Pass today's `validateSummarizerSchemaInvariant`. */
  passes_current: number;
  /** Pass the per-anchor check: today's invariant is that check, so this equals `passes_current`. */
  passes_per_anchor: number;
  /** Pass today's and fail the per-anchor check: 0, as the two are one check since the invariant took every anchor. */
  newly_refused: number;
}

/** A hub's counts. Deleted rows are left out, apart from `secret_shapes`. */
export interface DataExplorerDebugHubCounts {
  /** The hub's `hub_environment` (its column's CHECK admits only these two). */
  hub_environment: 'preview' | 'production' | null;
  /** Base Toolset connections, and those holding a credential. */
  toolset_connections: { total: number; with_credential: number };
  placeholders: Record<DebugTextFamily, DebugPlaceholderCounts>;
  /** The knowledge placeholders over every family: catalog and file content. */
  knowledge_placeholders: number;
  /**
   * The summary schemas (wayai-pro/wayai#5744): the live Summarizer agents' `schema_json`, and the
   * reserved conversation-scoped summary state's `json_schema`.
   */
  summary_schemas: { summarizer_agent: DebugSummarySchemaCounts; summary_state: DebugSummarySchemaCounts };
  /**
   * Where secrets are stored and in what form, by shape only (wayai-pro/wayai#5644 step 1): every
   * column of `CONNECTION_CREDENTIAL_COLUMNS` and `TOOL_CREDENTIAL_COLUMNS` but `token_refresh_config`,
   * the connector catalog's refresh settings.
   */
  secret_shapes: {
    connection_headers: DebugSecretColumnShape;
    /** Its fields but `username`, which is stored as text by design. */
    encrypted_credentials: DebugSecretColumnShape;
    tool_headers: DebugSecretColumnShape;
    tool_query_params: DebugSecretColumnShape;
    additional_body_params: DebugSecretColumnShape;
    /** Whether each connection's base URL fixes an origin (`credentialOrigin`), the origin rule's home. */
    base_url: { fixed_origin: number; none: number };
  };
}

/** A production hub's publish state: its previews, and what a publish of each would change. */
export interface DataExplorerDebugPublishState {
  previews: number;
  /** Previews a publish would change production from. */
  previews_with_changes: number;
  /** The entries those publishes would change, summed (hub settings count one). */
  changes: number;
}

/** An organization's counts. Deleted org resources are left out. */
export interface DataExplorerDebugOrgCounts {
  /** The org's live skills (knowledge is retired, #5142 step 5). */
  org_resources: { skills: number };
  /** Links of a hub to an org resource. */
  resource_links: number;
  /** The `knowledge_file_type` registry: org resources' moves, and hub resources'. */
  knowledge_file_types: { org: number; hub: number };
  /** Org credentials' `encrypted_*` metadata fields, by shape only (wayai-pro/wayai#5644 step 1). */
  secret_shapes: { credential_metadata: DebugSecretColumnShape };
}
